
import { useState, useEffect, useRef } from "react";
import { Search, X, Clock, TrendingUp } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { useDebounce } from "@/hooks/useDebounce";
import { getSearchSuggestions, getSearchHistory, saveSearchToHistory, clearSearchHistory } from "@/services/searchService";
import { useNavigate } from "@/lib/router-compat";
import { cn } from "@/lib/utils";
import { T } from "@/i18n/T";

interface UnifiedSearchProps {
  placeholder?: string;
  onSearch?: (query: string) => void;
  value?: string;
  onChange?: (value: string) => void;
  mode?: "global" | "page";
  showHistory?: boolean;
  showSuggestions?: boolean;
  isLoading?: boolean;
  className?: string;
}

const UnifiedSearch = ({
  placeholder = "Search...",
  onSearch,
  value: controlledValue,
  onChange,
  mode = "page",
  showHistory = true,
  showSuggestions = true,
  isLoading = false,
  className = ""
}: UnifiedSearchProps) => {
  const [internalValue, setInternalValue] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [searchHistory, setSearchHistory] = useState<any[]>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  
  const debouncedQuery = useDebounce(internalValue, 300);
  const searchRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Use controlled or internal value
  const currentValue = controlledValue !== undefined ? controlledValue : internalValue;
  const setValue = controlledValue !== undefined ? onChange : setInternalValue;

  // Load search history on mount
  useEffect(() => {
    if (showHistory) {
      setSearchHistory(getSearchHistory());
    }
  }, [showHistory]);

  // Get suggestions when query changes
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (showSuggestions && debouncedQuery.length >= 2) {
        setIsLoadingSuggestions(true);
        try {
          const newSuggestions = await getSearchSuggestions(debouncedQuery);
          setSuggestions(newSuggestions);
        } catch (error) {
          console.error('Error fetching suggestions:', error);
        } finally {
          setIsLoadingSuggestions(false);
        }
      } else {
        setSuggestions([]);
      }
    };

    fetchSuggestions();
  }, [debouncedQuery, showSuggestions]);

  // Handle clicks outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (searchQuery?: string) => {
    const finalQuery = searchQuery || currentValue;
    
    if (mode === "global" && finalQuery.trim()) {
      // Global mode - navigate to search page
      navigate(`/search?q=${encodeURIComponent(finalQuery)}`);
      setShowDropdown(false);
      setValue?.("");
      return;
    }
    
    if (mode === "page" && onSearch) {
      // Page mode - call the provided onSearch function
      onSearch(finalQuery);
      if (finalQuery.trim()) {
        saveSearchToHistory(finalQuery, { sources: [], categories: [], dateRange: "all" });
        setSearchHistory(getSearchHistory());
      }
    }
    
    setShowDropdown(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setValue?.(suggestion);
    handleSearch(suggestion);
  };

  const handleHistoryClick = (historyItem: any) => {
    setValue?.(historyItem.query);
    handleSearch(historyItem.query);
  };

  const clearQuery = () => {
    setValue?.("");
    setShowDropdown(false);
  };

  const shouldShowDropdown = showDropdown && (
    (suggestions.length > 0 && showSuggestions) || 
    (searchHistory.length > 0 && showHistory && currentValue.length === 0)
  );

  return (
    <div className={`relative ${className}`} ref={searchRef}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={placeholder}
          value={currentValue}
          onChange={(e) => {
            setValue?.(e.target.value);
            setShowDropdown(true);
          }}
          onKeyDown={handleKeyPress}
          onFocus={() => setShowDropdown(true)}
          className={cn(
            "pl-9 h-9 rounded-[10px] border-border/50 bg-background/50 backdrop-blur-sm",
            "focus:border-primary/50 focus:ring-2 focus:ring-primary/20 transition-all duration-200",
            currentValue && "pr-10"
          )}
          disabled={isLoading}
        />
        {currentValue && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7 p-0 hover:bg-accent/50"
            onClick={clearQuery}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      {shouldShowDropdown && (
        <Card className="absolute top-full left-0 right-0 z-50 mt-1 max-h-80 overflow-y-auto">
          <div className="p-2">
            {/* Search History */}
            {currentValue.length === 0 && searchHistory.length > 0 && showHistory && (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-medium flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    <T k="auto.recent-searches-5c79" />
                  </Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      clearSearchHistory();
                      setSearchHistory([]);
                    }}
                    className="text-xs"
                  >
                    <T k="auto.clear-dc30" />
                  </Button>
                </div>
                {searchHistory.slice(0, 5).map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 p-2 hover:bg-muted rounded cursor-pointer"
                    onClick={() => handleHistoryClick(item)}
                  >
                    <Clock className="h-3 w-3 text-muted-foreground" />
                    <span className="text-sm">{item.query}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Suggestions */}
            {suggestions.length > 0 && showSuggestions && (
              <div>
                <Label className="text-sm font-medium flex items-center gap-1 mb-2">
                  <TrendingUp className="h-3 w-3" />
                  <T k="auto.suggestions-41ec" />
                </Label>
                {suggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 p-2 hover:bg-muted rounded cursor-pointer"
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    <Search className="h-3 w-3 text-muted-foreground" />
                    <span className="text-sm">{suggestion}</span>
                  </div>
                ))}
              </div>
            )}

            {isLoadingSuggestions && (
              <div className="p-2 text-sm text-muted-foreground"><T k="auto.loading-suggestions-7645" /></div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
};

export default UnifiedSearch;
